import {
  QueryClient,
  UseSuspenseQueryOptions,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

import { ApiError } from '@/models/ApiErrorData.ts';
import {
  AcceptedVariables,
  CreateQueryOptions,
  FilteredUseQueryOptions,
} from '@/queries/utils/CreateQuery.ts';

/**
 * Same as the options of useSuspenseQuery but with some fields removed
 * @see UseSuspenseQueryOptions
 *
 * queryKey: Already defined in the createQuery function and should not be modified
 * queryFn: Already defined in the createQuery function and should not be modified
 * throwOnError: Use useSuspenseQuery / createSuspenseQuery instead
 * meta: Do not use if possible (untyped)
 * queryClient: Should use the default queryClient from the context (remove from omit only if you know what you are doing)
 *
 */
export type FilteredUseSuspenseQueryOptions<Data, TransformData = Data> = Omit<
  UseSuspenseQueryOptions<Data, ApiError, TransformData>,
  'queryFn' | 'queryKey' | 'networkMode' | 'meta' | 'queryClient'
>;

/**
 * Creates a new query
 * Returns a useQuery hook with an already defined behavior (the queryKey, queryFn and networkMode are already set) and some default options
 *
 * Usage:
 * - First call createQuery with a function and a query key outside the component tree. You can also add other options like retry, staleTime, etc.
 * ```ts
 * type UsePatientQueryVariables = {
 *    patientId: string;
 * };
 *
 * export const usePatientQuery = createSuspenseQuery(
 *    name: 'usePatientQuery',
 *    queryKey: (variables) => [variables.id],
 *    queryFn: async ({ patientId }: UsePatientQueryVariables) => {
 *        // Your API / Storage call here
 *        return await fetchPatient(patientId);
 *    }, {
 *        staleTime: 1000 * 60 * 5, // 5 minutes, we want to refresh our patients regularly
 *        // The only option that is not part of useQuery is onSuccess. It is just called after the queryFn is successful
 *        onSuccess: (resultData, variables, context) => {
 *            // In there you have access to the context, so it can be used to invalidate other queries, update the cache, etc.
 *        }
 *    }
 *    );
 * ```
 *
 * - Then use the hook in your component
 * ```tsx
 * const patientQuery = usePatientQuery({ // First parameter is the variables
 *    patientId: '123'
 * }, { // Second parameter is the options, same as useQuery with some fields removed (see FilteredUseSuspenseQueryOptions)
 *    staleTime: 1000 * 60, // 1 minute, Query options can also be overridden here
 * });
 * ```
 *
 * - Also, the hook has some additional properties
 *   They can be used outside the component tree
 * ```ts
 * // Get the query key for a specific set of variables
 * const queryKey = usePatientQuery.getQueryKey({ patientId: '123' });
 * // Manually update the query data
 * usePatientQuery.manualUpdate(queryClient, { patientId: '123' }, (data) => ({ ...data, name: 'John' }));
 * // Invalidate the query
 * usePatientQuery.invalidate({ patientId: '123' });
 * ```
 */
export const createSuspenseQuery = <Variables extends AcceptedVariables, Data>(
  queryName: string,
  queryKey: (variables: Partial<Variables>) => string[],
  queryFn: (variables: Variables) => Data | Promise<Data>,
  createQueryOptions?: CreateQueryOptions<Variables, Data>,
) => {
  const useCustomUseQuery = <TransformData = Data>(
    variables: Variables,
    options?: FilteredUseSuspenseQueryOptions<Data, TransformData>,
  ) => {
    const queryClient = useQueryClient();
    const result = useSuspenseQuery<Data, ApiError, TransformData>(
      {
        queryKey: [queryName, ...queryKey(variables)],
        queryFn: async context => {
          const result = await queryFn(variables);
          await createQueryOptions?.onSuccess?.(result, variables, {
            ...context,
            queryClient,
          });
          return result;
        },
        ...createQueryOptions,
        ...options,
      },
      queryClient,
    );
    if (result.status === 'error') {
      throw result.error;
    }
    return result.data;
  };
  useCustomUseQuery.getQueryKey = (variables: Partial<Variables>) => [
    queryName,
    ...queryKey(variables),
  ];
  useCustomUseQuery.queryName = queryName;
  useCustomUseQuery.manualUpdate = (
    queryClient: QueryClient,
    variables: Partial<Variables>,
    updater: (data: Data) => Data,
  ) => {
    queryClient.setQueryData(useCustomUseQuery.getQueryKey(variables), updater);
  };
  useCustomUseQuery.invalidate = (
    queryClient: QueryClient,
    variables: Partial<Variables>,
  ) => {
    queryClient.invalidateQueries({
      queryKey: useCustomUseQuery.getQueryKey(variables),
    });
  };
  useCustomUseQuery.clear = (
    queryClient: QueryClient,
    variables: Partial<Variables>,
  ) => {
    queryClient.resetQueries({
      queryKey: useCustomUseQuery.getQueryKey(variables),
    });
  };
  useCustomUseQuery.prefetch = <TransformData = Data>(
    queryClient: QueryClient,
    variables: Variables,
    options?: FilteredUseQueryOptions<Data, TransformData>,
  ) => {
    queryClient.prefetchQuery({
      queryKey: useCustomUseQuery.getQueryKey(variables),
      queryFn: async context => {
        const result = await queryFn(variables);
        await createQueryOptions?.onSuccess?.(result, variables, {
          ...context,
          queryClient,
        });
        return result;
      },
      ...createQueryOptions,
      ...options,
    });
  };

  return useCustomUseQuery;
};
